import { useEffect, useRef } from "react";
import { useAuth } from "@/hooks/useAuth";
import { useOrganizationTheme } from "@/hooks/use-organization-theme";
import { useTheme } from "@/hooks/use-theme";
import { initIntercom, updateIntercom, shutdownIntercom } from "@/lib/intercom";

/** Maps TechAlign theme values to Intercom's theme_mode. */
function toIntercomTheme(theme: string): 'light' | 'dark' {
  return theme === 'light' ? 'light' : 'dark';
}

/**
 * Initializes Intercom with the authenticated user's identity, JWT verification,
 * organization branding color, and the current light/dark theme.
 * Renders nothing — purely a side-effect component.
 */
export function IntercomProvider() {
  const { user, isAuthenticated } = useAuth();
  const { organization } = useOrganizationTheme();
  const { theme } = useTheme();
  const primaryColor = organization?.settings?.primaryColor;
  const intercomBooted = useRef(false);

  // Boot Intercom when user authenticates
  useEffect(() => {
    if (isAuthenticated && user) {
      initIntercom({
        id: user.id,
        firstName: user.firstName ?? undefined,
        lastName: user.lastName ?? undefined,
        email: user.email ?? '',
        createdAt: user.createdAt ?? undefined,
        primaryColor: primaryColor || undefined,
        themeMode: toIntercomTheme(theme),
      }).catch((err) => {
        console.warn('Intercom initialization failed:', err);
      });
      intercomBooted.current = true;
    }

    return () => {
      shutdownIntercom();
      intercomBooted.current = false;
    };
  }, [isAuthenticated, user, primaryColor]);

  // Push theme changes to Intercom without a full re-boot
  useEffect(() => {
    if (intercomBooted.current) {
      updateIntercom({ theme_mode: toIntercomTheme(theme) });
    }
  }, [theme]);

  return null;
}
